import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import type * as React from 'react';
import CanyouLogo from '../../assets/canyou-blue-logo.svg';
import Dog from '../../assets/canyou-dog.svg';
import { links, type NavGroup, navGroups } from '../foundation/links';
import { CtaButton } from '../foundation/primitives';
import { colors } from '../foundation/tokens';

const Chevron: React.FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const navText = {
  fontSize: '15px',
  fontWeight: 500,
  color: colors.ink70,
  whiteSpace: 'nowrap' as const,
  _hover: { color: colors.ink },
  _focusVisible: {
    outline: `3px solid ${colors.blue}`,
    outlineOffset: '4px',
    borderRadius: '4px',
  },
};

const DesktopGroup: React.FC<{ group: NavGroup }> = ({ group }) => {
  if (!group.items) {
    return (
      <Box as="a" href={group.href} {...navText}>
        {group.label}
      </Box>
    );
  }
  return (
    <Menu placement="bottom-start" gutter={14} isLazy>
      <MenuButton
        {...navText}
        display="inline-flex"
        alignItems="center"
        gap="5px"
        _expanded={{ color: colors.ink }}
      >
        <Flex align="center" gap="5px">
          {group.label}
          <Chevron />
        </Flex>
      </MenuButton>
      <MenuList
        bg={colors.white}
        className="cy-surface"
        minW="300px"
        p="8px"
        border="2px solid"
        borderColor={colors.ink}
        borderRadius="14px"
        boxShadow={`6px 6px 0 ${colors.blue}`}
        zIndex={70}
      >
        {group.items.map((item) => (
          <MenuItem
            key={item.label}
            as="a"
            href={item.href}
            borderRadius="10px"
            px="12px"
            py="10px"
            _hover={{ bg: '#E8EEFF' }}
            _focus={{ bg: '#E8EEFF' }}
            _focusVisible={{
              bg: '#E8EEFF',
              outline: `3px solid ${colors.blue}`,
              outlineOffset: '-3px',
            }}
          >
            <Box>
              <Text m={0} fontSize="15px" fontWeight={700} color={colors.ink}>
                {item.label}
              </Text>
              {item.desc && (
                <Text m={0} fontSize="13px" color={colors.ink70}>
                  {item.desc}
                </Text>
              )}
            </Box>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

/** Site-wide header for every page. */
export const SiteNav: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex={60}
      bg={colors.navBg}
      backdropFilter="blur(12px)"
      borderBottom="2px solid"
      borderColor={colors.ink}
    >
      <Flex
        maxW="1180px"
        mx="auto"
        px={{ base: '20px', md: '28px' }}
        py="14px"
        align="center"
        gap="18px"
      >
        <Box
          as="a"
          href="/"
          aria-label="Canyou home"
          mr="auto"
          display="flex"
          alignItems="center"
          gap={{ base: '8px', md: '10px' }}
        >
          {/* Brandmark: Apollo in a chair, same pairing as the footer. */}
          <Box
            aria-hidden="true"
            w={{ base: '26px', md: '36px' }}
            flexShrink={0}
            sx={{
              svg: { width: '100%', height: 'auto', display: 'block' },
              // Canyou Blue to match the wordmark (the file is black).
              'svg path': { fill: colors.blue },
              'svg .st0-dog': { stroke: colors.blue },
            }}
          >
            <Dog />
          </Box>
          {/* Smaller on phones so the brandmark fits beside the menu. */}
          <Box
            w={{ base: '112px', md: '140px' }}
            sx={{ svg: { width: '100%', height: 'auto', display: 'block' } }}
          >
            <CanyouLogo />
          </Box>
        </Box>
        <Flex
          as="nav"
          aria-label="Main"
          display={{ base: 'none', xl: 'flex' }}
          align="center"
          gap="22px"
        >
          {navGroups.map((g) => (
            <DesktopGroup key={g.label} group={g} />
          ))}
        </Flex>
        <Flex
          align="center"
          gap="10px"
          ml={{ xl: '12px' }}
          display={{ base: 'none', md: 'flex' }}
        >
          <Box
            as="a"
            href={links.login}
            color={colors.ink}
            fontSize="15px"
            fontWeight={600}
            whiteSpace="nowrap"
            px="6px"
            py="10px"
            _hover={{ color: colors.blue }}
          >
            Log in
          </Box>
          <CtaButton
            href={links.signup}
            size="sm"
            event="Site: Clicked Try for free (nav)"
          >
            Try for free
          </CtaButton>
          <CtaButton
            href={links.demo}
            size="sm"
            tone="white"
            event="Site: Clicked Book a demo (nav)"
          >
            Book a demo
          </CtaButton>
        </Flex>
        {/* Phones: keep the primary action visible next to the menu. */}
        <Box display={{ base: 'block', md: 'none' }} ml="auto" mr="4px">
          <CtaButton
            href={links.signup}
            size="sm"
            event="Site: Clicked Try for free (phone header)"
          >
            Try for free
          </CtaButton>
        </Box>
        <IconButton
          aria-label="Open menu"
          icon={<MenuIcon />}
          display={{ base: 'inline-flex', xl: 'none' }}
          variant="ghost"
          minW="44px"
          h="44px"
          onClick={onOpen}
        />
      </Flex>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent bg={colors.page} color={colors.ink}>
          <DrawerCloseButton size="lg" />
          <DrawerBody pt="64px" pb="32px">
            <Stack spacing="22px">
              {navGroups.map((g) =>
                g.items ? (
                  <Box key={g.label}>
                    <Text
                      m={0}
                      mb="8px"
                      fontSize="12px"
                      fontWeight={800}
                      letterSpacing="0.1em"
                      textTransform="uppercase"
                      color={colors.muted}
                    >
                      {g.label}
                    </Text>
                    <Stack spacing="10px">
                      {g.items.map((item) => (
                        <Box
                          as="a"
                          key={item.label}
                          href={item.href}
                          onClick={onClose}
                          fontSize="17px"
                          fontWeight={600}
                          color={colors.ink}
                          _focusVisible={{
                            outline: `3px solid ${colors.blue}`,
                            outlineOffset: '4px',
                            borderRadius: '4px',
                          }}
                        >
                          {item.label}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ) : (
                  <Box
                    as="a"
                    key={g.label}
                    href={g.href}
                    onClick={onClose}
                    fontSize="17px"
                    fontWeight={700}
                    color={colors.ink}
                    _focusVisible={{
                      outline: `3px solid ${colors.blue}`,
                      outlineOffset: '4px',
                      borderRadius: '4px',
                    }}
                  >
                    {g.label}
                  </Box>
                ),
              )}
              <Box
                as="a"
                href={links.login}
                fontSize="17px"
                fontWeight={700}
                color={colors.ink}
                _focusVisible={{
                  outline: `3px solid ${colors.blue}`,
                  outlineOffset: '4px',
                  borderRadius: '4px',
                }}
              >
                Log in
              </Box>
              <CtaButton
                href={links.signup}
                event="Site: Clicked Try for free (mobile nav)"
              >
                Try for free
              </CtaButton>
              <CtaButton
                href={links.demo}
                tone="white"
                event="Site: Clicked Book a demo (mobile nav)"
              >
                Book a demo
              </CtaButton>
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

const MenuIcon: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);
